import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Copy, Share2, Gift, CheckCircle, Facebook, Instagram, Link2, MessageCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// X (formerly Twitter) icon component
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InviteFriendsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useApp();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/${user.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setStats(d))
      .catch(() => {});
  }, [user?.id]);

  if (!isAuthenticated) {
    return (
      <Layout title="Invite Friends">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <Users className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in to invite friends</h2>
          <p className="text-gray-500 text-center mb-4">Earn rewards for every friend you invite</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700">
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  // Real referral code + stats from the backend
  const referralCode = stats?.referral_code || user?.referral_code || '…';
  const referralLink = `${window.location.origin}/invite/${referralCode}`;

  const userReferralData = {
    totalInvites: stats?.total_invites || 0,
    pendingRewards: stats?.pending_rewards || 0,
    earnedRewards: stats?.earned_rewards || 0,
    referredUsers: stats?.referred_users || []
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Link copied to clipboard!');
  };

  const copyCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Code copied to clipboard!');
  };

  const shareToSocial = (platform) => {
    const message = `Join me on Minted - the best place to buy and sell near you! Use my code ${referralCode} to get started. ${referralLink}`;
    
    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent(message)}`;
        break;
      case 'x':
        url = `https://x.com/intent/tweet?text=${encodeURIComponent(message)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(message)}`;
        break;
      default:
        copyLink();
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <Layout title="Invite Friends" showBack>
      {/* Hero */}
      <div className="bg-gradient-to-br from-minted-600 to-minted-700 px-4 py-8 text-white text-center">
        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Gift className="w-8 h-8" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Invite Friends, Earn $5</h1>
        <p className="text-minted-100">Get $5 for every friend who makes their first purchase</p>
      </div>

      {/* Stats */}
      <div className="flex bg-white border-b">
        <div className="flex-1 p-4 text-center border-r">
          <p className="text-2xl font-bold text-gray-900" data-testid="referral-total-invites">{userReferralData.totalInvites}</p>
          <p className="text-xs text-gray-500">Friends Invited</p>
        </div>
        <div className="flex-1 p-4 text-center border-r">
          <p className="text-2xl font-bold text-yellow-600">${userReferralData.pendingRewards.toFixed(2)}</p>
          <p className="text-xs text-gray-500">Pending</p>
        </div>
        <div className="flex-1 p-4 text-center">
          <p className="text-2xl font-bold text-minted-600">${userReferralData.earnedRewards.toFixed(2)}</p>
          <p className="text-xs text-gray-500">Earned</p>
        </div>
      </div>

      {/* Referral Code */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Your Referral Code</h3>
        <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold text-minted-600 tracking-wider" data-testid="referral-code-display">{referralCode}</p>
            <p className="text-xs text-gray-500">Share this code with friends</p>
          </div>
          <Button variant="outline" size="sm" onClick={copyCode} data-testid="referral-copy-code-btn">
            <Copy className="w-4 h-4 mr-1" />
            Copy
          </Button>
        </div>
      </div>

      {/* Share Link */}
      <div className="px-4 pb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Share Your Link</h3>
        <div className="flex gap-2">
          <Input value={referralLink} readOnly className="flex-1 bg-gray-50" />
          <Button onClick={copyLink} className="bg-minted-600 hover:bg-minted-700" data-testid="referral-copy-link-btn">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Social Share */}
      <div className="px-4 pb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Share via</h3>
        <div className="grid grid-cols-4 gap-3">
          <button
            onClick={() => shareToSocial('facebook')}
            className="flex flex-col items-center gap-1 p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            <Facebook className="w-6 h-6 text-blue-600" />
            <span className="text-xs text-blue-600">Facebook</span>
          </button>
          <button
            onClick={() => shareToSocial('instagram')}
            className="flex flex-col items-center gap-1 p-3 rounded-lg bg-pink-50 hover:bg-pink-100 transition-colors"
          >
            <Instagram className="w-6 h-6 text-pink-600" />
            <span className="text-xs text-pink-600">Instagram</span>
          </button>
          <button
            onClick={() => shareToSocial('x')}
            className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <XIcon className="w-6 h-6 text-black" />
            <span className="text-xs text-black">X</span>
          </button>
          <button
            onClick={() => shareToSocial('whatsapp')}
            className="flex flex-col items-center gap-1 p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors"
          >
            <MessageCircle className="w-6 h-6 text-green-600" />
            <span className="text-xs text-green-600">WhatsApp</span>
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="px-4 py-4 bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-4">How it works</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-minted-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
            <div>
              <p className="font-medium text-gray-900">Share your code</p>
              <p className="text-sm text-gray-500">Send your referral code or link to friends</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-minted-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
            <div>
              <p className="font-medium text-gray-900">Friend signs up</p>
              <p className="text-sm text-gray-500">They create an account using your code</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-minted-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
            <div>
              <p className="font-medium text-gray-900">You earn $5</p>
              <p className="text-sm text-gray-500">$5 lands in your Minted wallet when they make their first purchase</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Referrals - only show if user has referrals */}
      {userReferralData.referredUsers.length > 0 && (
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Your Referrals</h3>
          <div className="space-y-2" data-testid="referral-list">
            {userReferralData.referredUsers.map((referral, index) => (
              <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-minted-100 rounded-full flex items-center justify-center">
                    <span className="text-minted-600 font-medium">{referral.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{referral.name}</p>
                    <p className="text-xs text-gray-500">Joined {new Date(referral.joinedAt).toLocaleDateString()}</p>
                  </div>
                </div>
                {referral.status === 'active' ? (
                  <CheckCircle className="w-5 h-5 text-minted-600" />
                ) : (
                  <span className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">Pending</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state for new users */}
      {userReferralData.referredUsers.length === 0 && (
        <div className="p-4">
          <div className="bg-minted-50 rounded-lg p-4 text-center">
            <Users className="w-10 h-10 text-minted-400 mx-auto mb-2" />
            <p className="text-sm text-minted-700 font-medium">No referrals yet</p>
            <p className="text-xs text-minted-600">Share your code to start earning rewards!</p>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default InviteFriendsPage;